import { FolderOpen, Eye, EyeSlash } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';

export default function GroupInspector({ groupId }: { groupId: string }) {
  const { layerGroups, layers, renameLayerGroup, toggleLayerVisibility } = useSceneStore();

  const group = layerGroups.find((g) => g.id === groupId);
  if (!group) return null;

  const childLayers = layers.filter((l) => l.groupId === groupId);
  const visibleCount = childLayers.filter((l) => l.visible).length;

  return (
    <div className="flex flex-col gap-0">
      {/* Group identity */}
      <div className="px-4 py-3 border-b border-border-subtle flex items-center gap-3">
        <FolderOpen size={18} className="text-accent shrink-0" />
        <div className="flex-1 min-w-0">
          <input
            value={group.name}
            onChange={(e) => renameLayerGroup(groupId, e.target.value || group.name)}
            onKeyDown={(e) => e.stopPropagation()}
            className="w-full bg-transparent text-sm font-sans font-medium text-text-primary focus:outline-none focus:border-b focus:border-accent pb-px"
          />
          <div className="text-[10px] font-mono text-text-tertiary mt-0.5">
            {childLayers.length} layer{childLayers.length !== 1 ? 's' : ''} · {visibleCount} visible
          </div>
        </div>
      </div>

      {/* Layer summary list */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <div className="text-[10px] font-semibold font-sans text-text-tertiary uppercase tracking-widest mb-2">
          Layers in group
        </div>
        {childLayers.length === 0 ? (
          <p className="text-xs font-sans text-text-tertiary italic">No layers in this group</p>
        ) : (
          <div className="space-y-1">
            {childLayers.map((layer) => (
              <div
                key={layer.id}
                className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-bg-hover transition-colors group"
                onClick={() => useSceneStore.getState().selectLayer(layer.id)}
              >
                {/* Type badge */}
                <span className={`text-[9px] font-mono px-1 rounded shrink-0 ${
                  layer.layerType === 'image'
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : 'bg-accent/15 text-accent'
                }`}>
                  {layer.layerType === 'image' ? 'IMG' : 'SP'}
                </span>
                <span className="flex-1 text-xs font-sans text-text-secondary truncate cursor-pointer hover:text-text-primary">
                  {layer.name}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLayerVisibility(layer.id);
                    getSpineManager().setLayerVisible(layer.id, !layer.visible);
                  }}
                  className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary transition-all duration-fast"
                >
                  {layer.visible ? <Eye size={11} /> : <EyeSlash size={11} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
